import { useEffect, memo, useState, useMemo } from "react";
import {
  PerformanceTrackerLineGraph,
  TimeSelectionSegmentedView,
} from "@wavvest/wealth-ui";
import "./index.scss";
import { WealthChartData } from "@/types/wealth";
import { TimeRange } from "@/api/custom";
import { fetchApexChartData } from "@/store/features/apex/apex-thunks";
import { fetchPlaidWealthSnapshot } from "@/store/features/plaid/plaid-thunks";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectApexAccounts } from "@/store/features/apex/apex-selectors";
import { selectPlaidAccounts } from "@/store/features/plaid/plaid-selectors";
import { selectUser } from "@/store/features/userinfo/userinfoSlice";

export type PerformanceTrackerProps = {
  // datapoints: Array<{ timestamp: number; value: number }>;
  chartData: WealthChartData;
  timeSelection: TimeRange;
  onTimeSelectionChange: (value: TimeRange) => void;
  onDataUpdate?: (data: {
    selectedTotal: number | null;
    lastTotal: number;
    delta: { value: number; percentage: number };
  }) => void;
  hideTimeSelection?: boolean;
};

const PerformanceTrackerBase = ({
  chartData,
  timeSelection,
  onTimeSelectionChange,
  onDataUpdate,
  hideTimeSelection = false,
}: PerformanceTrackerProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const activeUserId = user?.userId;
  const apexAccounts = useAppSelector(selectApexAccounts);
  const accountIds = apexAccounts.map((account) => account.id);
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const plaidAccountIds = plaidAccounts.map((acc) => acc.account_id);

  const [selectedTotal, setSelectedTotal] = useState<number | null>(null);

  const yValues = useMemo(
    () => chartData.values.map((v) => parseFloat(v)),
    [chartData.values],
  );

  const lastTotal = useMemo(
    () => (yValues.length > 0 ? yValues[yValues.length - 1] : 0),
    [yValues],
  );

  const previousTotal = useMemo(() => {
    if (yValues.length > 1) {
      return yValues[yValues.length - 2];
    }
    if (yValues.length === 1) {
      return yValues[0];
    }
    return 0;
  }, [yValues]);

  const delta = useMemo(
    () => calculateDelta(previousTotal, lastTotal),
    [previousTotal, lastTotal],
  );

  const fetchRange = (value: TimeRange) => {
    if (!activeUserId) {
      return;
    }
    dispatch(fetchApexChartData({ accountIds, range: value, activeUserId }));
    dispatch(
      fetchPlaidWealthSnapshot({ accountIds: plaidAccountIds, range: value }),
    );
  };

  const changeTimeSelection = (value: TimeRange) => {
    onTimeSelectionChange(value);
    fetchRange(value);
  };

  // With hideTimeSelection the range picker lives on the wealth page, which
  // also loads the chart for a new range (it stays mounted across tabs).

  const onCursorSelect = (_x: number, y: number) => {
    setSelectedTotal(y);
  };

  const onCursorRelease = () => {
    setSelectedTotal(null);
  };

  useEffect(() => {
    if (onDataUpdate) {
      onDataUpdate({
        selectedTotal,
        lastTotal,
        delta,
      });
    }
  }, [selectedTotal, lastTotal, delta, onDataUpdate]);

  if (chartData.values.length === 0) {
    console.error("Datapoints must have at least one element.");
    return null;
  }

  return (
    <div className="performance-tracker">
      {!hideTimeSelection && (
        <div className="performance-header">
          <div className="time-selection-left">
            <TimeSelectionSegmentedView
              value={timeSelection}
              onChange={changeTimeSelection}
            />
          </div>
        </div>
      )}
      <PerformanceTrackerLineGraph
        xValues={chartData.timestamps}
        yValues={yValues}
        onCursorSelect={onCursorSelect}
        onCursorRelease={onCursorRelease}
      />
    </div>
  );
};

function calculateDelta(
  x1: number,
  x2: number,
): {
  value: number;
  percentage: number;
} {
  if (x1 === 0) return { value: x2, percentage: 0 };
  const dx = x2 - x1;
  const fraction = dx / x1;
  return {
    value: dx,
    percentage: fraction * 100,
  };
}

const arePropsEqual = (
  prevProps: PerformanceTrackerProps,
  nextProps: PerformanceTrackerProps,
) => {
  // compare timeSelection
  if (prevProps.timeSelection !== nextProps.timeSelection) {
    return false;
  }

  // compare onTimeSelectionChange
  if (prevProps.onTimeSelectionChange !== nextProps.onTimeSelectionChange) {
    return false;
  }

  // deep compare chartData
  const prevData = prevProps.chartData;
  const nextData = nextProps.chartData;

  if (
    prevData.timestamps.length !== nextData.timestamps.length ||
    prevData.values.length !== nextData.values.length
  ) {
    return false;
  }

  // compare array content
  for (let i = 0; i < prevData.timestamps.length; i++) {
    if (prevData.timestamps[i] !== nextData.timestamps[i]) {
      return false;
    }
  }

  for (let i = 0; i < prevData.values.length; i++) {
    if (prevData.values[i] !== nextData.values[i]) {
      return false;
    }
  }

  return true;
};

export {
  PerformanceTrackerLineGraph,
  TimeSelectionSegmentedView,
  rangeHasHistory,
} from "@wavvest/wealth-ui";

export const PerformanceTracker = memo(PerformanceTrackerBase, arePropsEqual);
